import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "../lib/cn";

/**
 * A bounded instrument: a titled, ruled surface for one reading.
 *
 * Deliberately not a Card. Card is a content container - roomy padding, no
 * rule under its header, no notion of state. A Panel is the frame around a
 * measurement on an operator console: dense, ruled, and able to say that what
 * it holds is live or failing. Both belong here; they answer different
 * questions, and collapsing them would mean a Card that knows what an alarm is.
 *
 * `feature` marks the dominant instrument on a screen with a gold top edge.
 * `live` rings it while something is streaming into it. `alarm` breathes red so
 * a failure is unmissable across a room - the one ambient animation on a
 * surface, and the shared stylesheet degrades it to a static ring under reduced
 * motion rather than leaving a critical state indistinguishable from a calm one.
 */
interface PanelProps extends HTMLAttributes<HTMLDivElement> {
  feature?: boolean;
  live?: boolean;
  alarm?: boolean;
}

interface PanelHeadProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
}

interface PanelBodyProps extends HTMLAttributes<HTMLDivElement> {
  /** No padding - for a table or map that should meet the panel's own edges. */
  tight?: boolean;
}

function PanelHead({ title, className, children, ...props }: PanelHeadProps) {
  return (
    <div
      data-slot="panel-head"
      className={cn(
        "flex items-center gap-2.5 border-b border-border px-3.5 py-[9px]",
        "group-data-[alarm]/panel:border-b-destructive/28",
        className,
      )}
      {...props}
    >
      {title != null && (
        <span
          data-slot="panel-title"
          className="text-[12.5px] font-semibold whitespace-nowrap text-foreground"
        >
          {title}
        </span>
      )}
      {children}
    </div>
  );
}

function PanelBody({ tight, className, children, ...props }: PanelBodyProps) {
  return (
    <div
      data-slot="panel-body"
      className={cn(tight ? "p-0" : "px-3.5 py-3", className)}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * The panel's surface on its own, for something that wants to sit on one without
 * being a panel - a stat tile is a bare figure, not a titled instrument, but it
 * has to look like it belongs beside one. Exported so that reads as the same
 * surface rather than a second copy of these values.
 */
export const panelSurface =
  "overflow-hidden rounded-lg border border-border " +
  "bg-[linear-gradient(180deg,var(--surface-raised),var(--surface))] shadow-[var(--shadow-panel)]";

export function Panel({ feature, live, alarm, className, children, ...props }: PanelProps) {
  return (
    <div
      data-slot="panel"
      data-feature={feature ? "" : undefined}
      data-live={live ? "" : undefined}
      data-alarm={alarm ? "" : undefined}
      className={cn(
        "group/panel",
        panelSurface,
        feature && "shadow-[var(--shadow-panel),inset_0_2px_0_0_var(--accent)]",
        live && "shadow-[var(--shadow-panel),0_0_0_1px_color-mix(in_srgb,var(--accent)_28%,transparent)]",
        alarm && "animate-panel-alarm border-destructive/28",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

Panel.Head = PanelHead;
Panel.Body = PanelBody;
